import { IndicationItem, IndicationStats } from 'features/indicationTriage'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'
import { z } from 'zod'

export enum GeckoDRA {
  GR_DRA_1 = 'GR-DRA-1',
  GR_DRA_2 = 'GR-DRA-2',
  GR_DRA_3 = 'GR-DRA-3',
  GR_DRA_4 = 'GR-DRA-4',
  GR_DRA_5 = 'GR-DRA-5',
  GR_DRA_6 = 'GR-DRA-6',
}

export enum GeckoSubDRA {
  NRI = 'NRI',
  I1 = 'I',
  LI1 = 'L/I-1',
  LI2 = 'L/I-2',
  LB1 = 'L/B-1',
  LB2 = 'L/B-2',
  COR1 = 'COR-1',
  COR2 = 'COR-2',
  COR3 = 'COR-3',
  COR4 = 'COR-4',
  CR1 = 'CR-1',
  CR2 = 'CR-2',
  CR3 = 'CR-3',
  CR4 = 'CR-4',
  SWC1 = 'SWC-1',
  SWC2 = 'SWC-2',
  SOHIC1 = 'SOHIC-1',
}

export const DRA_SUB_MAPPINGS: Record<GeckoDRA, GeckoSubDRA[]> = {
  [GeckoDRA.GR_DRA_1]: [GeckoSubDRA.NRI, GeckoSubDRA.I1, GeckoSubDRA.LI1],
  [GeckoDRA.GR_DRA_2]: [GeckoSubDRA.COR1, GeckoSubDRA.LB1, GeckoSubDRA.LI2],
  [GeckoDRA.GR_DRA_3]: [GeckoSubDRA.COR2, GeckoSubDRA.LB2, GeckoSubDRA.CR1],
  [GeckoDRA.GR_DRA_4]: [GeckoSubDRA.COR3, GeckoSubDRA.CR2],
  [GeckoDRA.GR_DRA_5]: [GeckoSubDRA.COR4, GeckoSubDRA.SWC1, GeckoSubDRA.CR3],
  [GeckoDRA.GR_DRA_6]: [GeckoSubDRA.SWC2, GeckoSubDRA.CR4, GeckoSubDRA.SOHIC1],
}

// SubDRA to DRA reverse mappings
export const SUB_DRA_MAPPINGS: Record<GeckoSubDRA, GeckoDRA> = Object.entries(
  DRA_SUB_MAPPINGS
).reduce(
  (acc, [category, subCategories]) => {
    const newAcc = { ...acc }
    subCategories.forEach((subCategory) => {
      newAcc[subCategory] = category as GeckoDRA
    })
    return newAcc
  },
  {} as Record<GeckoSubDRA, GeckoDRA>
)

// Type-safe helper functions
export const getSubDRAsForDRA = (dra: GeckoDRA): GeckoSubDRA[] => {
  return DRA_SUB_MAPPINGS[dra] || []
}

export const getDRAForSubDRA = (subDRA: GeckoSubDRA): GeckoDRA | undefined => {
  return SUB_DRA_MAPPINGS[subDRA]
}

export interface RautReportInfo {
  stats: IndicationStats
  dra_rating: GeckoDRA
  sub_rating: GeckoSubDRA
  is_rotated: boolean
  depth_conversion: number
  ascan_distance_conversion: number
  sensor_distance_conversion: number
}
/**
 * Base indication type with all possible properties
 */
export interface RautReportIndication extends Omit<
  IndicationItem,
  'customInfo'
> {
  customInfo: RautReportInfo
}

const IndicationStatsSchema = z.object({
  minimum: z.number(),
  mean: z.number(),
  maximum: z.number(),
  std_dev: z.number(),
  min_coords: z.tuple([z.number(), z.number()]),
  max_coords: z.tuple([z.number(), z.number()]),
})

const GeckoDRASchema = z.enum(GeckoDRA)
const GeckoSubDRASchema = z.enum(GeckoSubDRA)

const RautReportInfoSchema = z.object({
  stats: IndicationStatsSchema,
  dra_rating: GeckoDRASchema,
  sub_rating: GeckoSubDRASchema,
  is_rotated: z.boolean(),
  depth_conversion: z.number(),
  ascan_distance_conversion: z.number(),
  sensor_distance_conversion: z.number(),
})

function upgradeStats(
  stats: z.infer<typeof IndicationStatsSchema>
): IndicationStats {
  return {
    ...stats,
    minimum: GeckoMeasurement.CreateFromValue(
      stats.minimum,
      GeckoMeasurementUnit.Meters
    ),
    mean: GeckoMeasurement.CreateFromValue(
      stats.mean,
      GeckoMeasurementUnit.Meters
    ),
    maximum: GeckoMeasurement.CreateFromValue(
      stats.maximum,
      GeckoMeasurementUnit.Meters
    ),
    std_dev: GeckoMeasurement.CreateFromValue(
      stats.std_dev,
      GeckoMeasurementUnit.Meters
    ),
  }
}

export function rautReportCustomInfoCast(
  customInfo: Record<string, unknown> | undefined
): RautReportInfo | undefined {
  // Early return if customInfo is missing
  if (!customInfo) {
    return undefined
  }

  // Strict validation using Zod
  const validationResult = RautReportInfoSchema.safeParse(customInfo)

  if (!validationResult.success) {
    console.warn(
      `Invalid indication data for indication ${customInfo}:`,
      validationResult.error.message
    )
    return undefined
  }

  const upgradedStats = upgradeStats(validationResult.data.stats)

  return {
    ...validationResult.data,
    stats: upgradedStats,
  }
}
